'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Book, ChevronRight, Map as MapIcon } from 'lucide-react';
import { LessonNode, GamificationState } from './gamified-tree';

// Helper to extract the slug from the filename
const getSlug = (id: string) => id.replace('.md', '');

interface NextLessonCardProps {
  gamificationState: GamificationState;
  focusedNode?: LessonNode | null;
  onNodeClick: (node: LessonNode) => void;
}

export default function NextLessonCard({ gamificationState, focusedNode, onNodeClick }: NextLessonCardProps) {
  
  const getLessonSubtitle = (fileNode?: LessonNode | null, fallbackNode?: LessonNode | null) => {
    if (!fileNode) return fallbackNode?.title || '';
    
    const parts = fileNode.path.split('/');
    // e.g. lessons/admin/our-business-structure-studies/dev/01-file.md
    if (parts.length >= 4) {
      const roleSlug = parts[parts.length - 2];
      if (roleSlug !== 'lessons' && roleSlug !== 'personal' && roleSlug !== 'human') {
        const unitSlugRaw = parts[parts.length - 3];
        if (unitSlugRaw && unitSlugRaw !== 'lessons') {
          const unitName = unitSlugRaw.replace(/-/g, ' ');
          return `${unitName}: ${roleSlug}`;
        }
      }
    }
    return fallbackNode?.title || fileNode.title;
  };

  let prerequisiteCount = 0;

  const findNextLesson = (nodes: LessonNode[]): LessonNode | null => {
    let foundNode: LessonNode | null = null;
    for (const node of nodes) {
      if (node.type === 'file') {
        const isLockedByRole = node.userHasRole === false;
        if (isLockedByRole) continue; // Skip files locked by role

        const state = gamificationState[getSlug(node.id)];
        
        if (state?.isGamified) {
          if (state.status === 'locked') {
             prerequisiteCount++;
             continue;
          }
          if (state.status === 'luminescent') {
             continue; // Mastered, skip
          }
        }
        
        // Either active gamified, or non-gamified (treated as incomplete for now)
        if (!foundNode) {
          foundNode = node;
        }
      } else {
        const childFound = findNextLesson(node.children);
        if (childFound && !foundNode) {
          foundNode = childFound;
        }
      }
    }
    return foundNode;
  };

  const nextLessonNode = focusedNode ? findNextLesson(focusedNode.children) : null;

  if (!focusedNode) return null;

  return (
    <div className="w-full">
      {nextLessonNode ? (
        <div className="w-full">
          <motion.button
            whileHover={nextLessonNode.userHasRole !== false ? { scale: 1.02 } : {}}
            whileTap={nextLessonNode.userHasRole !== false ? { scale: 0.98 } : {}}
            onClick={() => {
              if (nextLessonNode.userHasRole !== false) {
                onNodeClick(nextLessonNode);
              }
            }}
            disabled={nextLessonNode.userHasRole === false}
            className={`w-full text-left bg-primary/10 border border-primary/30 p-4 rounded-xl flex items-center gap-4 transition-colors shadow-[0_0_15px_rgba(var(--primary),0.15)] ${nextLessonNode.userHasRole === false ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/20'}`}
          >
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shrink-0 shadow-lg">
              <Book className="w-6 h-6 text-primary-foreground " />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-foreground leading-tight">{nextLessonNode.title}</h3>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 capitalize">
                {getLessonSubtitle(nextLessonNode, focusedNode)}
              </p>
              {prerequisiteCount > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{prerequisiteCount} more lessons pending</p>
              )}
            </div>
            {nextLessonNode.userHasRole === false ? (
              <Lock className="w-6 h-6 text-muted-foreground opacity-50" />
            ) : (
              <ChevronRight className="w-6 h-6 text-primary opacity-50" />
            )}
          </motion.button>
        </div>
      ) : (
        <div className="w-full bg-muted/30 border border-border p-4 rounded-xl flex flex-col gap-2">
          <div className="flex items-center gap-4">
             <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center shrink-0">
              <Lock className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-muted-foreground">All Available Lessons Completed</h3>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 capitalize">
                {getLessonSubtitle(null, focusedNode)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
